// Implements: F-SHR-003, F-SHR-008, F-UIX-007
/**
 * The share popover's content (UX-06, the mockup's `.share-pop`). Pure presentation: the draft,
 * the versions, the answer and the links come as props.
 *
 * The form: what is shared (Prototipo or Diagrama) and which version (always the latest, or one
 * fixed) as segmented controls, the version picker for a fixed one, the expiry (mockup v3 P8: the
 * server's default first, then "7 días", "30 días", "90 días" and "1 año"; the server's own
 * range still applies and its sentence is shown), "Proteger con
 * contraseña" as a switch that reveals the password, the label, and "Crear enlace". The password
 * states no minimum of its own: the server's setting is the rule and its sentence names it.
 *
 * Once the server answers, the form gives way to the link, its copy action and the warning that it
 * is shown only now, with whether the password was set (or, if the link came without one, what to
 * do). "Crear otro" starts again; closing drops the link. The mockup's own links are listed under
 * it, and the foot leads to the page that manages every link.
 *
 * Mockup v3 P8: "Ver como visitante" sits beside "Crear enlace" and, once the link exists, beside
 * "Crear otro" (`onVisitor`): it shows what the link opens and never counts a visit.
 */

import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { Switch } from "../../../shared/ui/atoms/Switch";
import { Field, describedBy, hintId } from "../../../shared/ui/molecules/Field";
import { SegmentedControl } from "../../../shared/ui/molecules/SegmentedControl";
import type { ShareArtifact, ShareMode } from "../../../types/sharing";
import { expiryChoiceOf } from "../model/expiryDays";
import type { ExpiryChoice } from "../model/expiryDays";
import { CopyLinkButton } from "./CopyLinkButton";
import type { Schedule } from "./CopyLinkButton";
import { ExpiryOptions } from "./ExpiryOptions";
import { DEFAULT_EXPIRY } from "../model/expiryDays";
import styles from "./SharePanel.module.css";

export interface ShareDraft {
  readonly artifact: ShareArtifact;
  readonly mode: ShareMode;
  /** The frozen version; ignored for a live link. */
  readonly versionId: string;
  /** The chosen expiry: a new link starts on 30 days (owner decision 2026-10-19, as in v3). */
  readonly expiry: ExpiryChoice;
  readonly label: string;
  /** "Proteger con contraseña": off sends no password, whatever was typed. */
  readonly protect: boolean;
  /** As typed. Never kept beyond the popover. */
  readonly password: string;
}

/** What became of the password the person typed, once the link exists. */
export type PasswordOutcome =
  | { readonly status: "none" }
  | { readonly status: "set" }
  /** A password was typed, yet the server answered a link without one: never shown as protected. */
  | { readonly status: "unprotected" };

export interface VersionChoice {
  readonly id: string;
  readonly label: string;
}

export type VersionChoices =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly choices: readonly VersionChoice[] };

export interface SharePanelProps {
  /** The id of the popover's heading, which names it. */
  readonly titleId: string;
  /** Prefix of the field ids, one per popover. */
  readonly idBase: string;
  readonly mockupName: string;
  readonly draft: ShareDraft;
  readonly onChange: (patch: Partial<ShareDraft>) => void;
  readonly versions: VersionChoices;
  readonly refusal?: string | undefined;
  readonly pending: boolean;
  /** The absolute link, right after the creation; `null` while the form is shown. */
  readonly createdUrl: string | null;
  readonly passwordOutcome?: PasswordOutcome | undefined;
  readonly onSubmit: () => void;
  readonly onAgain: () => void;
  readonly onClose: () => void;
  /** Opens what a visitor of this link (made or about to be) sees. */
  readonly onVisitor?: (() => void) | undefined;
  /** The mockup's links (the list and its states), drawn under the form. */
  readonly links: ReactNode;
  /** How many of the mockup's links are active, once known. */
  readonly activeCount: number | null;
  readonly schedule?: Schedule | undefined;
}

export function SharePanel({
  titleId,
  idBase,
  mockupName,
  draft,
  onChange,
  versions,
  refusal,
  pending,
  createdUrl,
  passwordOutcome = { status: "none" },
  onSubmit,
  onAgain,
  onClose,
  onVisitor,
  links,
  activeCount,
  schedule,
}: SharePanelProps): ReactElement {
  const { t } = useTranslation();
  const expiryId = `${idBase}-expiry`;
  const passwordId = `${idBase}-password`;
  const labelId = `${idBase}-label`;

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <h2 id={titleId} className={styles.title}>
          {createdUrl === null ? null : <Icon name="check-circle" size="sm" />}
          {createdUrl === null ? t("sharing.dialog.title") : t("sharing.created.title")}
        </h2>
        <span className={styles.meta}>{mockupName}</span>
      </div>
      <div className={styles.body}>
        {createdUrl === null ? (
          <form
            className={styles.form}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            <div className={styles.row}>
              <span className={styles.label} aria-hidden="true">
                {t("sharing.artifact.label")}
              </span>
              <SegmentedControl<ShareArtifact>
                label={t("sharing.artifact.label")}
                options={[
                  { value: "prototype", label: t("sharing.artifact.prototype") },
                  { value: "diagram", label: t("sharing.artifact.diagram") },
                ]}
                value={draft.artifact}
                onChange={(artifact) => {
                  onChange({ artifact });
                }}
                disabled={pending}
              />
            </div>
            <div className={styles.row}>
              <span className={styles.label} aria-hidden="true">
                {t("sharing.mode.label")}
              </span>
              <SegmentedControl<ShareMode>
                label={t("sharing.mode.label")}
                options={[
                  { value: "live", label: t("sharing.mode.live") },
                  { value: "frozen", label: t("sharing.mode.frozen") },
                ]}
                value={draft.mode}
                onChange={(mode) => {
                  onChange({ mode });
                }}
                disabled={pending}
              />
            </div>
            {draft.mode === "frozen" ? (
              <VersionPicker id={`${idBase}-version`} draft={draft} onChange={onChange} versions={versions} pending={pending} />
            ) : null}
            {/* The mockup's compact rows: the label beside the field, the hint read out, not drawn. */}
            <div className={styles.row}>
              <label className={styles.label} htmlFor={expiryId}>
                {t("sharing.expiry.choice")}
              </label>
              <span className={styles.narrow}>
                <select
                  id={expiryId}
                  className={styles.select}
                  value={draft.expiry}
                  aria-describedby={hintId(expiryId)}
                  disabled={pending}
                  onChange={(event) => {
                    onChange({ expiry: expiryChoiceOf(event.target.value) ?? DEFAULT_EXPIRY });
                  }}
                >
                  <ExpiryOptions />
                </select>
              </span>
              {/* "1 año" lasts one year (owner 2026-10-19): said aloud, and drawn when chosen. */}
              <span id={hintId(expiryId)} className={draft.expiry === "never" ? styles.help : styles.srOnly}>
                {t("sharing.expiry.neverHelp")}
              </span>
            </div>
            <Switch
              label={t("sharing.password.toggle")}
              checked={draft.protect}
              disabled={pending}
              onChange={(protect) => {
                onChange({ protect });
              }}
            />
            {draft.protect ? (
              <Field id={passwordId} label={t("sharing.password.label")} hint={t("sharing.password.hint")}>
                <Input
                  id={passwordId}
                  type="password"
                  autoComplete="new-password"
                  value={draft.password}
                  describedBy={describedBy(passwordId, { hint: t("sharing.password.hint") })}
                  disabled={pending}
                  onChange={(password) => {
                    onChange({ password });
                  }}
                />
              </Field>
            ) : null}
            <div className={styles.row}>
              <label className={styles.label} htmlFor={labelId}>
                {t("sharing.label.label")}
              </label>
              <span className={styles.wide}>
                <Input
                  id={labelId}
                  value={draft.label}
                  describedBy={describedBy(labelId, { hint: t("sharing.label.hint") })}
                  disabled={pending}
                  onChange={(label) => {
                    onChange({ label });
                  }}
                />
              </span>
              <span id={hintId(labelId)} className={styles.srOnly}>
                {t("sharing.label.hint")}
              </span>
            </div>
            {refusal === undefined ? null : (
              <p role="alert" className={styles.refusal}>
                {refusal}
              </p>
            )}
            <span className={styles.submit}>
              {onVisitor === undefined ? null : <VisitorButton onClick={onVisitor} />}
              <Button variant="primary" type="submit" disabled={pending} loading={pending}>
                {t("sharing.create")}
              </Button>
            </span>
          </form>
        ) : (
          <div className={styles.form}>
            <CopyLinkButton url={createdUrl} schedule={schedule} />
            <p className={styles.notice} role="status">
              <Icon name="lock" size="sm" />
              <span>{t("sharing.created.once")}</span>
            </p>
            {passwordOutcome.status === "set" ? <p className={styles.help}>{t("sharing.created.protected")}</p> : null}
            {passwordOutcome.status === "unprotected" ? (
              <p role="alert" className={styles.refusal}>
                {t("sharing.created.unprotected")} {t("sharing.created.unprotectedNext")}
              </p>
            ) : null}
            <div className={styles.actions}>
              {onVisitor === undefined ? null : <VisitorButton onClick={onVisitor} />}
              <Button variant="ghost" size="sm" onClick={onAgain}>
                {t("sharing.again")}
              </Button>
              <Button variant="secondary" size="sm" onClick={onClose}>
                {t("common.action.close")}
              </Button>
            </div>
          </div>
        )}
        <section className={styles.links} aria-label={t("sharing.list.title")}>
          <h3 className={styles.subtitle}>{t("sharing.list.title")}</h3>
          {links}
        </section>
      </div>
      <div className={styles.foot}>
        <Link className={styles.manage} to="/sharing">
          {t("sharing.manageAll")}
          {activeCount === null || activeCount === 0 ? null : ` (${t("sharing.activeCount", { n: activeCount })})`}
          <span className={styles.arrow} aria-hidden="true">
            {t("sharing.manageArrow")}
          </span>
        </Link>
      </div>
    </div>
  );
}

function VisitorButton({ onClick }: { readonly onClick: () => void }): ReactElement {
  const { t } = useTranslation();
  return (
    <Button variant="secondary" size="sm" onClick={onClick}>
      <Icon name="eye" size="sm" />
      {t("sharing.visitor.open")}
    </Button>
  );
}

interface VersionPickerProps {
  readonly id: string;
  readonly draft: ShareDraft;
  readonly onChange: (patch: Partial<ShareDraft>) => void;
  readonly versions: VersionChoices;
  readonly pending: boolean;
}

function VersionPicker({ id, draft, onChange, versions, pending }: VersionPickerProps): ReactElement {
  const { t } = useTranslation();
  if (versions.status === "loading") return <p className={styles.help}>{t("sharing.version.loading")}</p>;
  if (versions.status === "error") return <p className={styles.refusal}>{t("sharing.version.failed")}</p>;
  return (
    <Field id={id} label={t("sharing.version.label")}>
      <select
        id={id}
        className={styles.select}
        value={draft.versionId}
        disabled={pending}
        onChange={(event) => {
          onChange({ versionId: event.target.value });
        }}
      >
        {versions.choices.map((choice) => (
          <option key={choice.id} value={choice.id}>
            {choice.label}
          </option>
        ))}
      </select>
    </Field>
  );
}
